import React, { createContext, useContext } from 'react';
import { getCurrentStreak, getLongestStreak, getWeeklyStreak } from '../utils/streakHelpers';

const AnalyticsContext = createContext(null);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};

/**
 * Analytics Context Provider
 * Provides statistics and data aggregation functions
 * Requires runs data from RunsContext
 */
export const AnalyticsProvider = ({ children, runs }) => {
  /**
   * Get stats for a set of days
   */
  const getStats = (wd) => {
    let completedTotal = 0, plannedTotal = 0, easy = 0;
    wd.forEach(d => {
      const r = runs[d.toDateString()];
      if (r) {
        const dist = r.distance || 0;
        if (r.type !== 'cross') {
          plannedTotal += dist;
          if (r.status === 'completed') {
            completedTotal += dist;
          }
        }
        if (r.type === 'easy') {
          easy += dist;
        }
      }
    });
    const total = completedTotal;
    const planned = plannedTotal;
    let ep = 0;
    if (total > 0) {
      ep = Math.round((easy / total) * 100);
    }
    return {
      total,
      planned,
      easy: ep,
      hard: 100 - ep
    };
  };

  /**
   * Get analytics data for last 7 days
   */
  const getLast7DaysData = () => {
    const data = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateKey = date.toDateString();
      const run = runs[dateKey];
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        miles: (run && run.status === 'completed' && run.type !== 'cross') ? (run.distance || 0) : 0
      });
    }
    return data;
  };

  /**
   * Get analytics data for last 4 weeks
   */
  const getLast4WeeksData = () => {
    const data = [];
    const today = new Date();
    for (let w = 3; w >= 0; w--) {
      let weekMiles = 0;
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - (w * 7 + 6));
      for (let d = 0; d < 7; d++) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + d);
        const dateKey = date.toDateString();
        const run = runs[dateKey];
        if (run && run.status === 'completed' && run.type !== 'cross') {
          weekMiles += run.distance || 0;
        }
      }
      data.push({
        week: `Week ${4 - w}`,
        miles: weekMiles
      });
    }
    return data;
  };

  /**
   * Get analytics data for last 365 days
   */
  const getLast365DaysData = () => {
    const data = [];
    const today = new Date();
    for (let m = 11; m >= 0; m--) {
      let monthMiles = 0;
      const monthDate = new Date(today);
      monthDate.setMonth(today.getMonth() - m);
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        const dateKey = date.toDateString();
        const run = runs[dateKey];
        if (run && run.status === 'completed' && run.type !== 'cross') {
          monthMiles += run.distance || 0;
        }
      }
      data.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short' }),
        miles: monthMiles
      });
    }
    return data;
  };

  /**
   * Get total mileage for last N days
   */
  const getTotalMileage = (days) => {
    const today = new Date();
    let total = 0;
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateKey = date.toDateString();
      const run = runs[dateKey];
      if (run && run.status === 'completed' && run.type !== 'cross') {
        total += run.distance || 0;
      }
    }
    return total.toFixed(1);
  };

  /**
   * Get streak statistics
   */
  const getStreakStats = () => {
    return {
      current: getCurrentStreak(runs),
      longest: getLongestStreak(runs),
      weekly: getWeeklyStreak(runs),
    };
  };

  const value = {
    getStats,
    getLast7DaysData,
    getLast4WeeksData,
    getLast365DaysData,
    getTotalMileage,
    getStreakStats,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
